import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import useAuthStore from '../store/useAuthStore';

const HATE_GUIDELINE_URL =
  'https://docs.google.com/document/d/1BFUf9l8klm6P6AV0Z1ORjf6KuQ4I_01u3QuoE7_DVdY/edit';
const MEME_GUIDELINE_URL =
  'https://docs.google.com/document/d/1b1YdCd7asQHCEJJaNgT1pbQvtZcBzU9PRU3-vtAV0aY/edit';

const buildGoogleLensUrl = (imageUrl) => {
  if (!imageUrl) return null;
  return `https://lens.google.com/uploadbyurl?url=${encodeURIComponent(imageUrl)}`;
};

const fetchAssignment = async () => {
  const response = await apiClient.get('/media/assignments');
  return response.data;
};

const fetchStats = async () => {
  const response = await apiClient.get('/media/stats');
  return response.data;
};

const fetchMyAnnotations = async () => {
  const response = await apiClient.get('/media/my-annotations');
  return response.data.items;
};

const submitAnnotation = async ({ id, annotationData }) => {
  const response = await apiClient.post(`/media/annotate/${id}`, annotationData);
  return response.data;
};

const AnnotatePanel = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const annotatorType = user?.annotatorType || 'hate';
  const isMemeAnnotator = annotatorType === 'meme';

  const LABEL_COLORS = {
    'non-hate': '#16a34a',
    hate: '#dc2626',
    unsure: '#d97706'
  };

  const MEME_CATEGORY_COLORS = {
    meme: '#7c3aed',
    'non-meme': '#0284c7',
    other: '#64748b'
  };

  const MEME_SUBCATEGORY_COLORS = {
    CM: '#f97316',
    FM: '#06b6d4',
    MI: '#16a34a',
    TS: '#8b5cf6',
    MT: '#ec4899'
  };

  const PROTECTED_CATEGORIES = [
    { value: 'race_or_ethnicity', label: 'Race or Ethnicity' },
    { value: 'religion', label: 'Religion' },
    { value: 'gender', label: 'Gender' },
    { value: 'disability_or_disease', label: 'Disability or Disease' },
    { value: 'nationality', label: 'Nationality' },
    { value: 'immigration_status', label: 'Immigration Status' },
    { value: 'socioeconomic_class', label: 'Socioeconomic Class' }
  ];

  const ATTACK_TYPES = [
    { value: 'comparison_to_animal', label: 'Comparison to Animal' },
    { value: 'comparison_to_object', label: 'Comparison to Object' },
    { value: 'comparison_with_criminals', label: 'Comparison with Criminals' },
    { value: 'exclusion', label: 'Exclusion' },
    { value: 'expressing_disgust_contempt', label: 'Expressing Disgust / Contempt' },
    { value: 'mental_physical_inferiority', label: 'Mental / Physical Inferiority' },
    { value: 'mocking_disability', label: 'Mocking Disability' },
    { value: 'mocking_hate_crime', label: 'Mocking Hate Crime' },
    { value: 'negative_stereotypes', label: 'Negative Stereotypes' },
    { value: 'use_of_slur', label: 'Use of Slur' },
    { value: 'violent_speech', label: 'Violent Speech' },
    { value: 'other', label: 'Other' }
  ];

  const formatLabel = (value) => {
    if (!value) return '';
    if (value === 'non-hate') return 'Non-Hate';
    if (value === 'unsure') return 'Unsure';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const formatMemeCategory = (value) => {
    if (!value) return '';
    if (value === 'non-meme') return 'Non meme';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const formatHateCategory = (value) => {
    const found = PROTECTED_CATEGORIES.find((c) => c.value === value);
    return found?.label || value;
  };

  const formatAttackType = (value) => {
    const found = ATTACK_TYPES.find((t) => t.value === value);
    return found?.label || value;
  };

  const [label, setLabel] = useState('');
  const [memeCategory, setMemeCategory] = useState('');
  const [memeSubcategory, setMemeSubcategory] = useState('');
  const [protectedCategories, setProtectedCategories] = useState([]);
  const [attackType, setAttackType] = useState('');
  const [unsureNote, setUnsureNote] = useState('');
  const [message, setMessage] = useState(null);
  const [viewMode, setViewMode] = useState('annotate'); // 'annotate' or 'history'
  const [historyIndex, setHistoryIndex] = useState(0);
  const [imageHistory, setImageHistory] = useState([]);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editMemeCategory, setEditMemeCategory] = useState('');
  const [editMemeSubcategory, setEditMemeSubcategory] = useState('');
  const [editProtectedCategories, setEditProtectedCategories] = useState([]);
  const [editAttackType, setEditAttackType] = useState('');
  const [editUnsureNote, setEditUnsureNote] = useState('');

  const {
    data: assignment,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['assignment'],
    queryFn: fetchAssignment,
    retry: false,
    // Critical: do not refetch and swap the image when the user
    // alt-tabs, opens Google Lens, or the browser regains focus.
    // That behavior can cause the in-progress item to disappear.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const item = assignment?.item;
  const assignmentMessage = assignment?.message;

  const {
    data: stats
  } = useQuery({
    queryKey: ['stats'],
    queryFn: fetchStats
  });

  const {
    data: myAnnotations = [],
    refetch: refetchHistory
  } = useQuery({
    queryKey: ['myAnnotations'],
    queryFn: fetchMyAnnotations,
    enabled: viewMode === 'history'
  });

  const mutation = useMutation({
    mutationFn: submitAnnotation,
    onSuccess: (data) => {
      // Success notifications take up vertical space and can interfere with the
      // next image layout on desktop. Keep the UI quiet on success.
      setMessage(null);
      if (item) {
        setImageHistory(prev => [{ mediaItem: item, annotation: data.annotation }, ...prev]);
      }
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['assignment'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      queryClient.invalidateQueries({ queryKey: ['myAnnotations'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to submit annotation';
      setMessage({ type: 'error', text: errorMessage });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ annotationId, annotationData }) => {
      const endpoint = isMemeAnnotator
        ? `/media/meme-annotation/${annotationId}`
        : `/media/annotation/${annotationId}`;
      const response = await apiClient.put(endpoint, annotationData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      setMessage(null);
      setEditingAnnotation(null);
      
      // Update history with new annotation data
      setImageHistory(prev => prev.map(entry => 
        entry.annotation._id === variables.annotationId 
          ? { ...entry, annotation: { ...entry.annotation, ...variables.annotationData } }
          : entry
      ));

      queryClient.invalidateQueries({ queryKey: ['myAnnotations'] });
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Failed to update annotation';
      setMessage({ type: 'error', text: errorMessage });
    }
  });

  const resetForm = useCallback(() => {
    if (isMemeAnnotator) {
      setMemeCategory('');
      setMemeSubcategory('');
      return;
    }

    setLabel('');
    setProtectedCategories([]);
    setAttackType('');
    setUnsureNote('');
  }, [isMemeAnnotator]);

  const triggerSubmit = (overrideData = {}) => {
    if (isMemeAnnotator) {
      const currentMemeCategory = overrideData.memeCategory || memeCategory;
      const currentMemeSubcategory = overrideData.memeSubcategory || memeSubcategory;

      if (!currentMemeCategory) {
        setMessage({ type: 'error', text: 'Please select meme category' });
        return;
      }

      if (currentMemeCategory === 'meme' && !currentMemeSubcategory) {
        setMessage({ type: 'error', text: 'Please select meme subcategory' });
        return;
      }

      const annotationData = {
        memeCategory: currentMemeCategory,
        memeSubcategory: currentMemeCategory === 'meme' ? currentMemeSubcategory : undefined
      };

      if (historyIndex > 0) {
        const hist = imageHistory[historyIndex - 1];
        if (hist && hist.annotation) {
          updateMutation.mutate({
            annotationId: hist.annotation._id,
            annotationData
          });
        }
      } else {
        mutation.mutate({
          id: item.id,
          annotationData
        });
      }

      return;
    }

    const currentLabel = overrideData.label || label;
    const currentProtectedCategories = overrideData.protectedCategories || protectedCategories;
    const currentAttackType = overrideData.attackType || attackType;
    const currentUnsureNote = overrideData.unsureNote ?? unsureNote;

    const annotationData = {
      label: currentLabel,
      unsureNote: currentLabel === 'unsure' ? currentUnsureNote : undefined,
      protectedCategories: currentLabel === 'hate' ? currentProtectedCategories : undefined,
      attackType: currentLabel === 'hate' ? currentAttackType : undefined,
      confusionReason: undefined,
      deleteReason: undefined,
      deleteReasonCustom: undefined
    };

    if (historyIndex > 0) {
      // Update existing annotation
      const hist = imageHistory[historyIndex - 1];
      if (hist && hist.annotation) {
        updateMutation.mutate({
          annotationId: hist.annotation._id,
          annotationData
        });
      }
    } else {
      // Create new annotation
      mutation.mutate({
        id: item.id,
        annotationData
      });
    }
  };

  const handleLabelChange = (newLabel) => {
    if (isMemeAnnotator) return;
    setLabel(newLabel);
    if (newLabel === 'non-hate') {
      triggerSubmit({ label: newLabel });
    }
  };

  const toggleProtectedCategory = (value) => {
    if (isMemeAnnotator) return;
    setProtectedCategories((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  };

  const handleMemeCategoryChange = (value) => {
    setMemeCategory(value);
    setMessage(null);

    if (isMemeAnnotator) {
      if (value !== 'meme') {
        triggerSubmit({ memeCategory: value, memeSubcategory: undefined });
      } else if (memeSubcategory) {
        triggerSubmit({ memeCategory: value });
      }
      return;
    }
  };

  const handleMemeSubcategoryChange = (value) => {
    setMemeSubcategory(value);
    setMessage(null);

    if (isMemeAnnotator) {
      if (memeCategory === 'meme') {
        triggerSubmit({ memeSubcategory: value });
      }
      return;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage(null);

    if (isMemeAnnotator) {
      triggerSubmit();
      return;
    }

    if (!label) {
      setMessage({ type: 'error', text: 'Please select a label' });
      return;
    }

    if (label === 'hate') {
      if (!protectedCategories || protectedCategories.length < 1) {
        setMessage({ type: 'error', text: 'Please select at least 1 protected category' });
        return;
      }
      if (!attackType) {
        setMessage({ type: 'error', text: 'Please select 1 type of attack' });
        return;
      }
    }

    triggerSubmit();
  };

  useEffect(() => {
    if (isMemeAnnotator) return;
    if (label !== 'hate') {
      setProtectedCategories([]);
      setAttackType('');
    }
    if (label !== 'unsure') setUnsureNote('');
  }, [label, isMemeAnnotator]);

  useEffect(() => {
    if (memeCategory !== 'meme') setMemeSubcategory('');
  }, [memeCategory]);

  const showSubmitButton = label === 'hate' || label === 'unsure';

  const currentHistoryItem = historyIndex > 0 ? imageHistory[historyIndex - 1] : null;
  const displayItem = historyIndex > 0 ? currentHistoryItem?.mediaItem : item;

  const handleOpenGoogleLens = () => {
    const url = buildGoogleLensUrl(displayItem?.url);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Populate form when navigating history
  useEffect(() => {
    if (historyIndex > 0 && imageHistory[historyIndex - 1]) {
      const hist = imageHistory[historyIndex - 1];
      const ann = hist.annotation;
      if (ann) {
        if (isMemeAnnotator) {
          setMemeCategory(ann.memeCategory || '');
          setMemeSubcategory(ann.memeSubcategory || '');
        } else {
          setLabel(ann.label || '');
          setUnsureNote(ann.unsureNote || '');
          setProtectedCategories(Array.isArray(ann.protectedCategories) ? ann.protectedCategories : []);
          setAttackType(ann.attackType || '');
        }
      }
    } else if (historyIndex === 0) {
      resetForm();
    }
  }, [historyIndex, imageHistory, isMemeAnnotator, resetForm]);


  const handleBack = () => {
    if (imageHistory.length > 0 && historyIndex < imageHistory.length) {
      setHistoryIndex(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
    }
  };

  const handleViewHistory = () => {
    setViewMode('history');
    refetchHistory();
  };

  const handleBackToAnnotate = () => {
    setViewMode('annotate');
    setHistoryIndex(0);
  };

  const handleEditAnnotation = (annotation) => {
    setEditingAnnotation(annotation);
    setEditLabel(annotation.label || '');
    setEditUnsureNote(annotation.unsureNote || '');
    setEditMemeCategory(annotation.memeCategory || '');
    setEditMemeSubcategory(annotation.memeSubcategory || '');
    setEditProtectedCategories(Array.isArray(annotation.protectedCategories) ? annotation.protectedCategories : []);
    setEditAttackType(annotation.attackType || '');
  };

  const handleCancelEdit = () => {
    setEditingAnnotation(null);
    setEditLabel('');
    setEditUnsureNote('');
    setEditMemeCategory('');
    setEditMemeSubcategory('');
    setEditProtectedCategories([]);
    setEditAttackType('');
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();

    if (isMemeAnnotator) {
      if (!editMemeCategory) {
        setMessage({ type: 'error', text: 'Please select meme category' });
        return;
      }

      if (editMemeCategory === 'meme' && !editMemeSubcategory) {
        setMessage({ type: 'error', text: 'Please select meme subcategory' });
        return;
      }

      updateMutation.mutate({
        annotationId: editingAnnotation.annotationId,
        annotationData: {
          memeCategory: editMemeCategory,
          memeSubcategory: editMemeCategory === 'meme' ? editMemeSubcategory : undefined
        }
      });
      return;
    }
    
    if (!editLabel) {
      setMessage({ type: 'error', text: 'Please select a label' });
      return;
    }

    if (editLabel === 'hate') {
      if (!editProtectedCategories || editProtectedCategories.length < 1) {
        setMessage({ type: 'error', text: 'Please select at least 1 protected category' });
        return;
      }
      if (!editAttackType) {
        setMessage({ type: 'error', text: 'Please select 1 type of attack' });
        return;
      }
    }

    updateMutation.mutate({
      annotationId: editingAnnotation.annotationId,
      annotationData: {
        label: editLabel,
        unsureNote: editLabel === 'unsure' ? editUnsureNote : undefined,
        protectedCategories: editLabel === 'hate' ? editProtectedCategories : undefined,
        attackType: editLabel === 'hate' ? editAttackType : undefined,
        confusionReason: undefined,
        deleteReason: undefined,
        deleteReasonCustom: undefined
      }
    });
  };

  return (
    <section className={`panel annotate ${viewMode === 'history' ? 'history-mode' : ''}`}>
      <div className="annotate-header">
        <div className="annotate-header-left">
          <h2>{viewMode === 'history' ? 'My Annotations' : 'Annotate Images'}</h2>

          {stats && (
            <div className="annotate-stats">
              <span>Total Images: {stats.totalImages}</span>
              {isMemeAnnotator ? (
                <span>
                  Need &lt;{stats.requiredMemeAnnotationsPerImage ?? 1} Meme Labels:{' '}
                  {stats.imagesWithLessThanRequiredMemeAnnotations ?? stats.imagesWithoutMemeAnnotation ?? 0}
                </span>
              ) : (
                <span>
                  Need &lt;{stats.requiredHateAnnotationsPerImage ?? 10} Labels:{' '}
                  {stats.imagesWithLessThanRequiredAnnotations ?? stats.imagesWithLessThan3}
                </span>
              )}
              <span>Your Annotations: {stats.userAnnotations}</span>
            </div>
          )}
        </div>
        <div className="header-actions">
          {viewMode === 'annotate' && imageHistory.length > 0 && (
            <>
              <button 
                type="button" 
                className="secondary small" 
                onClick={handleBack}
                disabled={historyIndex >= imageHistory.length}
              >
                ← Back
              </button>
              <button 
                type="button" 
                className="secondary small" 
                onClick={handleNext}
                disabled={historyIndex === 0}
              >
                Next →
              </button>
            </>
          )}
          {viewMode === 'annotate' ? (
            <>
              <button type="button" className="secondary" onClick={handleViewHistory}>
                View All
              </button>
              <a
                className="secondary"
                href={isMemeAnnotator ? MEME_GUIDELINE_URL : HATE_GUIDELINE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center' }}
                title={isMemeAnnotator ? 'Open meme annotation guideline' : 'Open hate annotation guideline'}
              >
                {isMemeAnnotator ? 'Meme Guideline' : 'Hate Guideline'}
              </a>
              <button type="button" className="secondary" onClick={() => { setHistoryIndex(0); refetch(); }}>
                Refresh
              </button>
            </>
          ) : (
            <button type="button" className="secondary" onClick={handleBackToAnnotate}>
              Back to Annotate
            </button>
          )}
        </div>
      </div>

      {isLoading && <p>Loading assignment…</p>}
      {isError && (
        <p className="message error">
          {error?.response?.data?.message || 'Failed to load assignment.'}
        </p>
      )}

      {!isLoading && !isError && !item && viewMode === 'annotate' && (
        <p>{assignmentMessage || 'No more images to annotate. Great work!'}</p>
      )}

      {viewMode === 'history' && (
        <div className="annotation-history">
          {myAnnotations.length === 0 ? (
            <p>No annotations yet. Start annotating to see your history here.</p>
          ) : (
            <div className="history-grid">
              {myAnnotations.map((annotation, idx) => (
                <div key={annotation.annotationId} className="history-card">
                  {annotation.mediaItem && (
                    <>
                      <img src={annotation.mediaItem.url} alt={annotation.mediaItem.originalName} />
                      <div className="history-details">
                        <h4>{annotation.mediaItem.originalName}</h4>
                        <div className="history-label">
                          {isMemeAnnotator ? (
                            <>
                              <span
                                className="sublabel"
                                style={{ '--badge-color': MEME_CATEGORY_COLORS[annotation.memeCategory] || '#64748b' }}
                                title="Meme category"
                              >
                                Meme: {annotation.memeCategory ? formatMemeCategory(annotation.memeCategory) : 'Not set'}
                              </span>
                              {annotation.memeCategory === 'meme' && (
                                <span
                                  className="sublabel"
                                  style={{ '--badge-color': MEME_SUBCATEGORY_COLORS[annotation.memeSubcategory] || '#64748b' }}
                                  title="Meme type"
                                >
                                  Type: {annotation.memeSubcategory || 'Not set'}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className={`label-badge ${annotation.label}`}>{formatLabel(annotation.label)}</span>
                              {Array.isArray(annotation.protectedCategories) && annotation.protectedCategories.length > 0 && (
                                <span className="sublabel" style={{ '--badge-color': '#7c3aed' }} title="Protected categories">
                                  Protected: {annotation.protectedCategories.map(formatHateCategory).join(', ')}
                                </span>
                              )}
                              {annotation.attackType && (
                                <span className="sublabel" style={{ '--badge-color': '#7c3aed' }} title="Type of attack">
                                  Attack: {formatAttackType(annotation.attackType)}
                                </span>
                              )}
                              {!annotation.attackType && !annotation.protectedCategories?.length && annotation.hateSubcategory && (
                                <span className="sublabel" style={{ '--badge-color': '#7c3aed' }} title="Legacy hate category">
                                  {annotation.hateSubcategory}
                                </span>
                              )}
                              {annotation.label === 'unsure' && annotation.unsureNote && (
                                <span className="sublabel" style={{ '--badge-color': '#64748b' }} title="Unsure note">
                                  Note: {annotation.unsureNote}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <p className="timestamp">{new Date(annotation.createdAt).toLocaleString()}</p>
                        <button 
                          type="button" 
                          className="edit-btn"
                          onClick={() => handleEditAnnotation(annotation)}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editingAnnotation && (
        <div className="edit-modal-overlay" onClick={handleCancelEdit}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>Edit Annotation</h3>
              <button type="button" className="close-btn" onClick={handleCancelEdit}>&times;</button>
            </div>
            <div className="edit-modal-content">
              {editingAnnotation.mediaItem && (
                <img src={editingAnnotation.mediaItem.url} alt={editingAnnotation.mediaItem.originalName} className="edit-preview" />
              )}
              <form onSubmit={handleSaveEdit}>
                {isMemeAnnotator ? (
                  <>
                    <h4>Meme category:</h4>
                    <div className="radio-group" style={{ marginBottom: '10px' }}>
                      {[
                        { value: 'meme', label: 'Meme' },
                        { value: 'non-meme', label: 'Non meme' },
                        { value: 'other', label: 'Other' }
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className={`radio-option ${editMemeCategory === opt.value ? 'selected' : ''}`}
                          style={{ '--opt-color': MEME_CATEGORY_COLORS[opt.value] || '#3b82f6' }}
                        >
                          <input
                            type="radio"
                            name="editMemeCategory"
                            value={opt.value}
                            checked={editMemeCategory === opt.value}
                            onChange={(e) => setEditMemeCategory(e.target.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>

                    {editMemeCategory === 'meme' && (
                      <>
                        <h4>Select meme type:</h4>
                        <div className="radio-group" style={{ marginBottom: '10px' }}>
                          {[
                            { value: 'CM', label: 'Character Macro (CM)' },
                            { value: 'FM', label: 'Format Macro (FM)' },
                            { value: 'MI', label: 'Memetic Images (MI)' },
                            { value: 'TS', label: 'Transferred Symbols (TS)' },
                            { value: 'MT', label: 'Memetic Trends (MT)' }
                          ].map((opt) => (
                            <label
                              key={opt.value}
                              className={`radio-option ${editMemeSubcategory === opt.value ? 'selected' : ''}`}
                              style={{ '--opt-color': MEME_SUBCATEGORY_COLORS[opt.value] || '#3b82f6' }}
                            >
                              <input
                                type="radio"
                                name="editMemeSubcategory"
                                value={opt.value}
                                checked={editMemeSubcategory === opt.value}
                                onChange={(e) => setEditMemeSubcategory(e.target.value)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <h4>Update Label:</h4>
                    <div className="label-options">
                      {['non-hate', 'hate', 'unsure'].map((labelOption) => (
                        <label
                          key={labelOption}
                          className={`label-button ${editLabel === labelOption ? 'selected' : ''}`}
                          style={{ '--opt-color': LABEL_COLORS[labelOption] || '#3b82f6' }}
                        >
                          <input
                            type="radio"
                            name="editLabel"
                            value={labelOption}
                            checked={editLabel === labelOption}
                            onChange={(e) => setEditLabel(e.target.value)}
                          />
                          <span>{formatLabel(labelOption)}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {!isMemeAnnotator && editLabel === 'hate' && (
                  <div className="subcategory-section">
                    <h4>Protected Category (multiple can be selected):</h4>
                    <div className="radio-group">
                      {PROTECTED_CATEGORIES.map((cat) => (
                        <label
                          key={cat.value}
                          className={`radio-option ${editProtectedCategories.includes(cat.value) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            name="editProtectedCategories"
                            value={cat.value}
                            checked={editProtectedCategories.includes(cat.value)}
                            onChange={() => {
                              setEditProtectedCategories((prev) => {
                                if (prev.includes(cat.value)) return prev.filter((v) => v !== cat.value);
                                return [...prev, cat.value];
                              });
                            }}
                          />
                          <span>{cat.label}</span>
                        </label>
                      ))}
                    </div>

                    <h4 style={{ marginTop: '1rem' }}>Type of attack (select one):</h4>
                    <div className="radio-group">
                      {ATTACK_TYPES.map((t) => (
                        <label key={t.value} className={`radio-option ${editAttackType === t.value ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="editAttackType"
                            value={t.value}
                            checked={editAttackType === t.value}
                            onChange={(e) => setEditAttackType(e.target.value)}
                          />
                          <span>{t.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {!isMemeAnnotator && editLabel === 'unsure' && (
                  <div className="comment-section">
                    <h4>Optional note (why unsure?):</h4>
                    <textarea
                      value={editUnsureNote}
                      onChange={(e) => setEditUnsureNote(e.target.value)}
                      placeholder="Optional: explain why you're unsure..."
                      rows="3"
                    />
                  </div>
                )}

                <div className="edit-modal-actions">
                  <button type="button" className="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button type="submit" className="primary" disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'annotate' && displayItem && (
        <div className="annotate-content">
          <div className="annotate-left">
            <div className="annotate-viewer">
              <img src={displayItem.url} alt={displayItem.originalName} />
            </div>
            <div className="image-meta">
              <p className="image-info">
                <strong>{displayItem.originalName}</strong> ({displayItem.annotationCount || 0} annotations)
              </p>
              {historyIndex > 0 && (
                <p className="history-notice">Viewing previous image ({historyIndex} back)</p>
              )}
            </div>
          </div>

          <form className="annotate-form" onSubmit={handleSubmit}>
            <div className="meme-context">
              <div className="meme-context-header">
                <h3>Meme context</h3>
                <div className="meme-context-actions">
                  <button type="button" className="secondary" onClick={handleOpenGoogleLens} disabled={!displayItem?.url}>
                    Open Google Lens
                  </button>
                </div>
              </div>
            </div>
            {isMemeAnnotator ? (
              <>
                <h3>Meme category:</h3>
                <div className="radio-group">
                  {[
                    { value: 'meme', label: 'Meme' },
                    { value: 'non-meme', label: 'Non meme' },
                    { value: 'other', label: 'Other' }
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`radio-option ${memeCategory === opt.value ? 'selected' : ''}`}
                      style={{ '--opt-color': MEME_CATEGORY_COLORS[opt.value] || '#3b82f6' }}
                    >
                      <input
                        type="radio"
                        name="memeCategory"
                        value={opt.value}
                        checked={memeCategory === opt.value}
                        onChange={(e) => handleMemeCategoryChange(e.target.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>

                {memeCategory === 'meme' && (
                  <>
                    <h4>Select meme type:</h4>
                    <div className="radio-group">
                      {[
                        { value: 'CM', label: 'Character Macro (CM)' },
                        { value: 'FM', label: 'Format Macro (FM)' },
                        { value: 'MI', label: 'Memetic Images (MI)' },
                        { value: 'TS', label: 'Transferred Symbols (TS)' },
                        { value: 'MT', label: 'Memetic Trends (MT)' }
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className={`radio-option ${memeSubcategory === opt.value ? 'selected' : ''}`}
                          style={{ '--opt-color': MEME_SUBCATEGORY_COLORS[opt.value] || '#3b82f6' }}
                        >
                          <input
                            type="radio"
                            name="memeSubcategory"
                            value={opt.value}
                            checked={memeSubcategory === opt.value}
                            onChange={(e) => handleMemeSubcategoryChange(e.target.value)}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <h3>Label this image:</h3>
                <div className="label-options">
                  <label className={`label-button ${label === 'non-hate' ? 'selected' : ''}`} style={{ '--opt-color': LABEL_COLORS['non-hate'] }}>
                    <input
                      type="radio"
                      name="label"
                      value="non-hate"
                      checked={label === 'non-hate'}
                      onChange={(e) => handleLabelChange(e.target.value)}
                    />
                    <span>Non-Hate</span>
                  </label>

                  <label className={`label-button ${label === 'hate' ? 'selected' : ''}`} style={{ '--opt-color': LABEL_COLORS.hate }}>
                    <input
                      type="radio"
                      name="label"
                      value="hate"
                      checked={label === 'hate'}
                      onChange={(e) => handleLabelChange(e.target.value)}
                    />
                    <span>Hate</span>
                  </label>

                  <label className={`label-button ${label === 'unsure' ? 'selected' : ''}`} style={{ '--opt-color': LABEL_COLORS.unsure }}>
                    <input
                      type="radio"
                      name="label"
                      value="unsure"
                      checked={label === 'unsure'}
                      onChange={(e) => handleLabelChange(e.target.value)}
                    />
                    <span>Unsure</span>
                  </label>
                </div>
              </>
            )}

            {!isMemeAnnotator && label === 'hate' && (
              <div className="subcategory-section">
                <h4>Protected Category (multiple can be selected):</h4>
                <div className="radio-group">
                  {PROTECTED_CATEGORIES.map((cat) => (
                    <label
                      key={cat.value}
                      className={`radio-option ${protectedCategories.includes(cat.value) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        name="protectedCategories"
                        value={cat.value}
                        checked={protectedCategories.includes(cat.value)}
                        onChange={() => toggleProtectedCategory(cat.value)}
                      />
                      <span>{cat.label}</span>
                    </label>
                  ))}
                </div>

                <h4 style={{ marginTop: '1rem' }}>Type of attack (select one):</h4>
                <div className="radio-group">
                  {ATTACK_TYPES.map((t) => (
                    <label
                      key={t.value}
                      className={`radio-option ${attackType === t.value ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="attackType"
                        value={t.value}
                        checked={attackType === t.value}
                        onChange={(e) => setAttackType(e.target.value)}
                      />
                      <span>{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {!isMemeAnnotator && label === 'unsure' && (
              <div className="subcategory-section">
                <h4>Optional note (why unsure?):</h4>
                <textarea
                  value={unsureNote}
                  onChange={(e) => setUnsureNote(e.target.value)}
                  placeholder="Optional: explain why you're unsure..."
                  rows={4}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            )}

            {!isMemeAnnotator && showSubmitButton && (
              <button
                type="submit"
                className="primary"
                disabled={
                  mutation.isPending ||
                  (label === 'hate' && (protectedCategories.length < 1 || !attackType))
                }
              >
                {mutation.isPending ? 'Submitting...' : 'Submit & Next'}
              </button>
            )}
          </form>

          {message?.type === 'error' && (
            <p className="message error">{message.text}</p>
          )}
        </div>
      )}
    </section>
  );
};

export default AnnotatePanel;
